import styled from 'styled-components'
import { motion } from 'framer-motion'
import { colors, getFontStyle } from '@diversioteam/diversio-ds'

import type { BellIconProps, NotificationBadgeProps } from './NotificationButton.types'

export const NotificationButtonContainer = styled.div`
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
`

export const BellButton = styled(motion.button)<BellIconProps>`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 40px;
    height: 40px;
    border: none;
    border-radius: 0.5rem;
    cursor: pointer;
    transition: all 0.2s ease;
    position: relative;
    color: ${colors.primary80};
    background: transparent;
    overflow: visible; /* allow ripple expansion */

    &:hover {
        background: ${colors.primaryGray1};
        color: ${colors.primary90};
    }

    &:active {
        background: ${colors.primaryGray2};
        transform: scale(0.95);
    }

    &:focus {
        outline: none;
    }

    ${(props) =>
        props.$isOpen &&
        `
    background: ${colors.primaryGray1};
    color: ${colors.primary90};
  `}

    ${(props) =>
        props.$isLoading &&
        `
    pointer-events: none;
    opacity: 0.6;
  `}
  
    /* Reduce motion for accessibility */
    @media (prefers-reduced-motion: reduce) {
        transition: none;
    }
`

export const BellIcon = styled(motion.div)<Pick<BellIconProps, '$isLoading'>>`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 20px;
    height: 20px;

    ${(props) =>
        props.$isLoading &&
        `
    animation: bellSpin 1s linear infinite;
  `}

    @keyframes bellSpin {
        from {
            transform: rotate(0deg);
        }
        to {
            transform: rotate(360deg);
        }
    }

    @media (prefers-reduced-motion: reduce) {
        animation: none !important;
    }
`

export const NotificationBadge = styled(motion.div)<NotificationBadgeProps>`
    position: absolute;
    top: -2px;
    right: -2px;
    min-width: 18px;
    height: 18px;
    background: ${colors.lobster60};
    color: ${colors.primaryWhite};
    border-radius: 9px;
    display: flex;
    align-items: center;
    justify-content: center;
    ${getFontStyle('captionBold')}
    font-size: 10px;
    line-height: 1;
    padding: 0 4px;
    border: 2px solid ${colors.primaryWhite};
    z-index: 1;

    ${(props) =>
        !props.$isVisible &&
        `
    display: none;
  `}
`

export const BellRipple = styled(motion.div)`
    position: absolute;
    width: 40px;
    height: 40px;
    border-radius: 9999px;
    background: ${colors.primary60};
    opacity: 0.12; /* subtle */
    z-index: 0; /* behind icon and badge */
`

// Animation variants for Framer Motion
/* Removed unused Framer Motion variant exports */

// Screen reader only text for accessibility
export const ScreenReaderText = styled.span`
    position: absolute !important;
    width: 1px !important;
    height: 1px !important;
    padding: 0 !important;
    margin: -1px !important;
    overflow: hidden !important;
    clip: rect(0, 0, 0, 0) !important;
    white-space: nowrap !important;
    border: 0 !important;
`
